import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import Select from '../Select';
import Typography from '../Typography';
import Button from '../Button';
import HotkeysPreferences from '../HotkeysPreferences';
import { ButtonEnums } from '../Button';
import { Form, Formik } from 'formik';
import toggleTools from '@custom/rtk-apis/ToolToogle';
import Loader from '../Loader/Loader';
import store from '@custom/store/store';
import { toggleOverlay } from '@custom/store/slices/ToggleTools';

const UserPreferences = ({
  availableLanguages,
  defaultLanguage,
  currentLanguage,
  disabled = false,
  hotkeyDefinitions,
  hotkeyDefaults,
  onCancel = () => {},
  onSubmit = () => {},
  onReset = () => {},
  hotkeysModule,
  dispatch,
  toggleButton,
  isToolVisible,
  isOverlayVisible,
  userDetails,
  toolButtonToggleResponse
}) => {
  const { t } = useTranslation('UserPreferencesModal');
  const [isDarkMode, setIsDarkMode] = useState(localStorage.getItem('isLightMode') == 'true' ? false : true)
  const [state, setState] = useState({
    isDisabled: disabled,
    hotkeyErrors: {},
    hotkeyDefinitions,
    language: currentLanguage,
  });

  const onSubmitHandler = () => {
    onSubmit(state);
  };

  const onResetHandler = () => {
    setState(state => ({
      ...state,
      language: defaultLanguage,
      hotkeyDefinitions: hotkeyDefaults,
      hotkeyErrors: {},
      isDisabled: disabled,
    }));
    onReset();
  };

  const onCancelHandler = () => {
    setState({ hotkeyDefinitions });
    onCancel();
  };

  const onLanguageChangeHandler = value => {
    setState(state => ({ ...state, language: value }));
  };

  const onHotkeysChangeHandler = (id, definition, errors) => {
    setState(state => ({
      ...state,
      isDisabled: Object.values(errors).every(e => e !== undefined),
      hotkeyErrors: errors,
      hotkeyDefinitions: {
        ...state.hotkeyDefinitions,
        [id]: definition,
      },
    }));
  };

  const Section = ({ title, children }) => (
    <>
      <div className="mb-2 border-b-2 border-black">
        <Typography
          variant="inherit"
          color="primaryLight"
          className={`flex pb-2 text-[16px] font-semibold !leading-[1.2] ${isDarkMode ? 'text-black' : ''}`}
        >
          {title}
        </Typography>
      </div>
      <div className={`mt-4 mb-8${isDarkMode ? ' text-black' : ''}`}>{children}</div>
    </>
  );

  useEffect(() => {
    setIsDarkMode(!isDarkMode);
  }, [localStorage.getItem('isLightMode')]);

  const [tools, setTools] = useState<any>([]);
  const [overlay, setOverlay] = useState<any>([]);

  const handleSubmit = (values) => {
    const {
      StudyPatientName,
      PatientId,
      PatientBirthDate,
      PatientSex,
      StudyID,
      StudyDate,
      ZoomLevel,
      InstanceNumber,
      WindowLevelOverlay,
      ...toolsSettings
    } = values; // Extract StudyPatientName and rest into toolsSettings

    const overlaySettings = {
      StudyPatientName,
      PatientId,
      PatientBirthDate,
      PatientSex,
      StudyID,
      StudyDate,
      ZoomLevel,
      InstanceNumber,
      WindowLevelOverlay
    }
    const currentButtonsState = store.getState().toolButtonToggleResponse.buttons;
    const currentOverlayState = store.getState().toolButtonToggleResponse.overlay;

    // Only dispatch toggleButton for buttons that have changed
    Object.keys(toolsSettings).forEach((key) => {
      if (toolsSettings[key] !== currentButtonsState[key]) {
        dispatch(toggleButton(key));
      }
    });
    Object.keys(overlaySettings).forEach((key) => {
      if (currentOverlayState[key] !== overlaySettings[key]) {
        dispatch(toggleOverlay(key));
      }
    });
    console.log('toolsSettings', toolsSettings);
    console.log('overlaySettings');

    // Sync both overlaySettings and toolsSettings to the database
    dispatch(
      toggleTools({
        toolsSettings,
        overlaySettings: {
          StudyPatientName,
          PatientId,
          PatientBirthDate,
          PatientSex,
          StudyID,
          StudyDate,

          ZoomLevel,
          InstanceNumber,
          WindowLevelOverlay
        },
      })
    ).then(() => {
      setTimeout(() => {
        window.location.reload();
      }, 900);
    });
  };


  // Fetch user details and update the tools
  useEffect(() => {
    dispatch(userDetails()).unwrap().then((res) => {
      console.log('result?.tools_settings', res?.result);
      setTools(res?.result?.tools_settings);
      setOverlay(res?.result?.overlay_settings);
    }).catch((err) => {
      console.log(err);
    })
  }, []);

  // useEffect(() => {
  //   if (tools) {
  //     Object.entries(tools).forEach(([buttonName, value]) => {
  //       dispatch(setButtonState({ buttonName, value }));
  //     });
  //   }
  // }, [tools]);


  return (
    <>
      <Section title={t('General')}>
        <div className="flex w-72 flex-row items-center justify-center">
          <Typography
            variant="subtitle"
            className={`mr-5 h-full text-right${isDarkMode ? ' text-black' : 'text-white'}`}
          >
            {t('Language')}
          </Typography>
          <Select
            isClearable={false}
            onChange={onLanguageChangeHandler}
            options={availableLanguages}
            value={state.language}
          />
        </div>
      </Section>
      <Section title={t('Hotkeys')}>
        <HotkeysPreferences
          disabled={disabled}
          hotkeyDefinitions={state.hotkeyDefinitions}
          onChange={onHotkeysChangeHandler}
          errors={state.hotkeyErrors}
          hotkeysModule={hotkeysModule}
        />
      </Section>
      <div className="flex flex-row justify-between">
        <Button
          type={ButtonEnums.type.secondary}
          onClick={onResetHandler}
          disabled={disabled}
        >
          {t('Reset to defaults')}
        </Button>
        <div className="flex flex-row">
          <Button
            type={ButtonEnums.type.secondary}
            onClick={onCancelHandler}
          >
            {t('Cancel')}
          </Button>
          <Button
            disabled={state.isDisabled}
            className="ml-2"
            onClick={onSubmitHandler}
          >
            {t('Save')}
          </Button>
        </div>
      </div>
      <div className="mt-8">

      <Formik
        initialValues={{
          Zoom: tools?.Zoom ?? isToolVisible.Zoom,
          WindowLevel: tools?.WindowLevel ?? isToolVisible.WindowLevel,
          Pan: tools?.Pan ?? isToolVisible.Pan,
          Capture: tools?.Capture ?? isToolVisible.Capture,

          TrackballRotate: tools?.TrackballRotate ?? isToolVisible.TrackballRotate,
          Layout: tools?.Layout ?? isToolVisible.Layout,
          AnnotationPreferences: tools?.AnnotationPreferences ?? isToolVisible.AnnotationPreferences,
          Crosshairs: tools?.Crosshairs ?? isToolVisible.Crosshairs,

          Length: tools?.Length ?? isToolVisible.Length,
          Bidirectional: tools?.Bidirectional ?? isToolVisible.Bidirectional,
          ArrowAnnotate: tools?.ArrowAnnotate ?? isToolVisible.ArrowAnnotate,
          EllipticalROI: tools?.EllipticalROI ?? isToolVisible.EllipticalROI,
          RectangleROI: tools?.RectangleROI ?? isToolVisible.RectangleROI,
          CircleROI: tools?.CircleROI ?? isToolVisible.CircleROI,
          PlanarFreehandROI: tools?.PlanarFreehandROI ?? isToolVisible.PlanarFreehandROI,
          SplineROI: tools?.SplineROI ?? isToolVisible.SplineROI,
          LivewireContour: tools?.LivewireContour ?? isToolVisible.LivewireContour,

          Reset: tools?.Reset ?? isToolVisible.Reset,
          'rotate-right': tools?.['rotate-right'] ?? isToolVisible['rotate-right'],
          flipHorizontal: tools?.flipHorizontal ?? isToolVisible.flipHorizontal,
          ImageSliceSync: tools?.ImageSliceSync ?? isToolVisible.ImageSliceSync,
          ReferenceLines: tools?.ReferenceLines ?? isToolVisible.ReferenceLines,
          ImageOverlayViewer: tools?.ImageOverlayViewer ?? isToolVisible.ImageOverlayViewer,
          StackScroll: tools?.StackScroll ?? isToolVisible.StackScroll,
          invert: tools?.invert ?? isToolVisible.invert,
          Probe: tools?.Probe ?? isToolVisible.Probe,
          Cine: tools?.Cine ?? isToolVisible.Cine,
          Angle: tools?.Angle ?? isToolVisible.Angle,
          CobbAngle: tools?.CobbAngle ?? isToolVisible.CobbAngle,
          Magnify: tools?.Magnify ?? isToolVisible.Magnify,
          CalibrationLine: tools?.CalibrationLine ?? isToolVisible.CalibrationLine,
          TagBrowser: tools?.TagBrowser ?? isToolVisible.TagBrowser,
          AdvancedMagnify: tools?.AdvancedMagnify ?? isToolVisible.AdvancedMagnify,
          UltrasoundDirectionalTool: tools?.UltrasoundDirectionalTool ?? isToolVisible.UltrasoundDirectionalTool,
          WindowLevelRegion: tools?.WindowLevelRegion ?? isToolVisible.WindowLevelRegion,
          toggleOverlay: tools?.toggleOverlay ?? isToolVisible.toggleOverlay,
          copyToClipboard: tools?.copyToClipboard ?? isToolVisible.copyToClipboard,

          MeasurementTools: tools?.MeasurementTools ?? isToolVisible.MeasurementTools,
          MoreTools: tools?.MoreTools ?? isToolVisible.MoreTools,

          StudyPatientName: overlay?.StudyPatientName ?? isOverlayVisible.StudyPatientName,
          PatientId: overlay?.PatientId ?? isOverlayVisible.PatientId,
          PatientBirthDate: overlay?.PatientBirthDate ?? isOverlayVisible.PatientBirthDate,
          PatientSex: overlay?.PatientSex ?? isOverlayVisible.PatientSex,

          StudyID: overlay?.StudyID ?? isOverlayVisible.StudyID,
          StudyDate: overlay?.StudyDate ?? isOverlayVisible.StudyDate,
          WindowLevelOverlay: overlay?.WindowLevelOverlay ?? isOverlayVisible.WindowLevelOverlay,
          ZoomLevel: overlay?.ZoomLevel ?? isOverlayVisible.ZoomLevel,
          InstanceNumber: overlay?.InstanceNumber ?? isOverlayVisible.InstanceNumber,
        }}
        onSubmit={(values) => {
          // Submit the form
          handleSubmit(values);
        }}
        enableReinitialize
      >
        {({ values, handleChange, handleBlur, setFieldValue }) =>{
          const selectAll = () => {
            const isAllSelected = Object.values(values).every((value) => value === true);
            // Toggle all fields based on current state
            Object.keys(values).forEach((key) => {
              setFieldValue(key, !isAllSelected);
            });
          };
          return (
            <>
              <div className="mb-2 border-b-2 border-black flex justify-between items-center">
                <Typography
                  variant="inherit"
                  color="primaryLight"
                  className={`flex pb-2 text-[16px] font-semibold !leading-[1.2] ${isDarkMode ? 'text-black' : ''}`}
                >
                  Tools Setting
                </Typography>
                <div className="form__checkbox">
                  <div className="form__checkbox__option m_0">
                    <input
                      className="form__checkbox__input"
                      type="checkbox"
                      id="SelectAll"
                      name="SelectAll"
                      onChange={selectAll}
                      checked={Object.values(values).every((value) => value === true)}
                    />
                    <label className="form__checkbox__label mr-5" htmlFor="Capture">
                      Select All
                    </label>
                  </div>
                </div>
              </div>
              <Form>
                <div className="formRow formRow__3">
                  <div className="formCol">
                    <div className="form__Field form__Field formRow formRow__2">
                      <div className="form__checkboxSubField">
                        <div className="form__checkbox">
                          <div className="form__checkbox__option m_0">
                            <input
                              className="form__checkbox__input"
                              type="checkbox"
                              id="Zoom"
                              name="Zoom"
                              checked={values.Zoom}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <label className="form__checkbox__label mr-5" htmlFor="Zoom">
                              Zoom
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="formCol">
                    <div className="form__Field form__Field formRow formRow__2">
                      <div className="form__checkboxSubField">
                        <div className="form__checkbox">
                          <div className="form__checkbox__option m_0">
                            <input
                              className="form__checkbox__input"
                              type="checkbox"
                              id="WindowLevel"
                              name="WindowLevel"
                              checked={values.WindowLevel}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <label className="form__checkbox__label mr-5" htmlFor="WindowLevel">
                              Window Level
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="formCol">
                    <div className="form__Field form__Field formRow formRow__2">
                      <div className="form__checkboxSubField">
                        <div className="form__checkbox">
                          <div className="form__checkbox__option m_0">
                            <input
                              className="form__checkbox__input"
                              type="checkbox"
                              id="Pan"
                              name="Pan"
                              checked={values.Pan}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <label className="form__checkbox__label mr-5" htmlFor="Pan">
                              Pan
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="formRow formRow__3">
                  <div className="formCol">
                    <div className="form__Field form__Field formRow formRow__2">
                      <div className="form__checkboxSubField">
                        <div className="form__checkbox">
                          <div className="form__checkbox__option m_0">
                            <input
                              className="form__checkbox__input"
                              type="checkbox"
                              id="Capture"
                              name="Capture"
                              checked={values.Capture}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <label className="form__checkbox__label mr-5" htmlFor="Capture">
                              Capture
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="formCol">
                    <div className="form__Field form__Field formRow formRow__2">
                      <div className="form__checkboxSubField">
                        <div className="form__checkbox">
                          <div className="form__checkbox__option m_0">
                            <input
                              className="form__checkbox__input"
                              type="checkbox"
                              id="TrackballRotate"
                              name="TrackballRotate"
                              checked={values.TrackballRotate}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <label className="form__checkbox__label mr-5" htmlFor="TrackballRotate">
                              Trackball Rotate
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="formCol">
                    <div className="form__Field form__Field formRow formRow__2">
                      <div className="form__checkboxSubField">
                        <div className="form__checkbox">
                          <div className="form__checkbox__option m_0">
                            <input
                              className="form__checkbox__input"
                              type="checkbox"
                              id="Layout"
                              name="Layout"
                              checked={values.Layout}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <label className="form__checkbox__label mr-5" htmlFor="Layout">
                              Layout
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="formRow formRow__3">
                  <div className="formCol">
                    <div className="form__Field form__Field formRow formRow__2">
                      <div className="form__checkboxSubField">
                        <div className="form__checkbox">
                          <div className="form__checkbox__option m_0">
                            <input
                              className="form__checkbox__input"
                              type="checkbox"
                              id="AnnotationPreferences"
                              name="AnnotationPreferences"
                              checked={values.AnnotationPreferences}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <label className="form__checkbox__label mr-5" htmlFor="AnnotationPreferences">
                              Annotation Preferences
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="formCol">
                    <div className="form__Field form__Field formRow formRow__2">
                      <div className="form__checkboxSubField">
                        <div className="form__checkbox">
                          <div className="form__checkbox__option m_0">
                            <input
                              className="form__checkbox__input"
                              type="checkbox"
                              id="Crosshairs"
                              name="Crosshairs"
                              checked={values.Crosshairs}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <label className="form__checkbox__label mr-5" htmlFor="Crosshairs">
                              Crosshairs
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="formCol">
                    <div className="form__Field form__Field formRow formRow__2">
                      <div className="form__checkboxSubField">
                        <div className="form__checkbox">
                          <div className="form__checkbox__option m_0">
                            <input
                              className="form__checkbox__input"
                              type="checkbox"
                              id="Bidirectional"
                              name="Bidirectional"
                              checked={values.Bidirectional}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <label className="form__checkbox__label mr-5" htmlFor="Bidirectional">
                              Bidirectional
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="formRow formRow__3">
                  <div className="formCol">
                    <div className="form__Field form__Field formRow formRow__2">
                      <div className="form__checkboxSubField">
                        <div className="form__checkbox">
                          <div className="form__checkbox__option m_0">
                            <input
                              className="form__checkbox__input"
                              type="checkbox"
                              id="ArrowAnnotate"
                              name="ArrowAnnotate"
                              checked={values.ArrowAnnotate}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <label className="form__checkbox__label mr-5" htmlFor="ArrowAnnotate">
                              Arrow Annotate
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="formCol">
                    <div className="form__Field form__Field formRow formRow__2">
                      <div className="form__checkboxSubField">
                        <div className="form__checkbox">
                          <div className="form__checkbox__option m_0">
                            <input
                              className="form__checkbox__input"
                              type="checkbox"
                              id="EllipticalROI"
                              name="EllipticalROI"
                              checked={values.EllipticalROI}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <label className="form__checkbox__label mr-5" htmlFor="EllipticalROI">
                              Elliptical ROI
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="formCol">
                    <div className="form__Field form__Field formRow formRow__2">
                      <div className="form__checkboxSubField">
                        <div className="form__checkbox">
                          <div className="form__checkbox__option m_0">
                            <input
                              className="form__checkbox__input"
                              type="checkbox"
                              id="RectangleROI"
                              name="RectangleROI"
                              checked={values.RectangleROI}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <label className="form__checkbox__label mr-5" htmlFor="RectangleROI">
                              Rectangle ROI
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="formRow formRow__3">
                  <div className="formCol">
                    <div className="form__Field form__Field formRow formRow__2">
                      <div className="form__checkboxSubField">
                        <div className="form__checkbox">
                          <div className="form__checkbox__option m_0">
                            <input
                              className="form__checkbox__input"
                              type="checkbox"
                              id="CircleROI"
                              name="CircleROI"
                              checked={values.CircleROI}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <label className="form__checkbox__label mr-5" htmlFor="CircleROI">
                              Circle ROI
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="formCol">
                    <div className="form__Field form__Field formRow formRow__2">
                      <div className="form__checkboxSubField">
                        <div className="form__checkbox">
                          <div className="form__checkbox__option m_0">
                            <input
                              className="form__checkbox__input"
                              type="checkbox"
                              id="PlanarFreehandROI"
                              name="PlanarFreehandROI"
                              checked={values.PlanarFreehandROI}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <label className="form__checkbox__label mr-5" htmlFor="PlanarFreehandROI">
                              Planar Freehand ROI
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="formCol">
                    <div className="form__Field form__Field formRow formRow__2">
                      <div className="form__checkboxSubField">
                        <div className="form__checkbox">
                          <div className="form__checkbox__option m_0">
                            <input
                              className="form__checkbox__input"
                              type="checkbox"
                              id="SplineROI"
                              name="SplineROI"
                              checked={values.SplineROI}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <label className="form__checkbox__label mr-5" htmlFor="SplineROI">
                              Spline ROI
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="formRow formRow__3">
                  <div className="formCol">
                    <div className="form__Field form__Field formRow formRow__2">
                      <div className="form__checkboxSubField">
                        <div className="form__checkbox">
                          <div className="form__checkbox__option m_0">
                            <input
                              className="form__checkbox__input"
                              type="checkbox"
                              id="LivewireContour"
                              name="LivewireContour"
                              checked={values.LivewireContour}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <label className="form__checkbox__label mr-5" htmlFor="LivewireContour">
                              Livewire Contour
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="formCol">
                    <div className="form__Field form__Field formRow formRow__2">
                      <div className="form__checkboxSubField">
                        <div className="form__checkbox">
                          <div className="form__checkbox__option m_0">
                            <input
                              className="form__checkbox__input"
                              type="checkbox"
                              id="rotate-right"
                              name="rotate-right"
                              checked={values['rotate-right']}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <label className="form__checkbox__label mr-5" htmlFor="rotate-right">
                              Rotate Right
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="formCol">
                    <div className="form__Field form__Field formRow formRow__2">
                      <div className="form__checkboxSubField">
                        <div className="form__checkbox">
                          <div className="form__checkbox__option m_0">
                            <input
                              className="form__checkbox__input"
                              type="checkbox"
                              id="flipHorizontal"
                              name="flipHorizontal"
                              checked={values.flipHorizontal}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <label className="form__checkbox__label mr-5" htmlFor="flipHorizontal">
                              Flip Horizontal
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="formRow formRow__3">
                  <div className="formCol">
                    <div className="form__Field form__Field formRow formRow__2">
                      <div className="form__checkboxSubField">
                        <div className="form__checkbox">
                          <div className="form__checkbox__option m_0">
                            <input
                              className="form__checkbox__input"
                              type="checkbox"
                              id="ReferenceLines"
                              name="ReferenceLines"
                              checked={values.ReferenceLines}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <label className="form__checkbox__label mr-5" htmlFor="ReferenceLines">
                              Reference Lines
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="formCol">
                    <div className="form__Field form__Field formRow formRow__2">
                      <div className="form__checkboxSubField">
                        <div className="form__checkbox">
                          <div className="form__checkbox__option m_0">
                            <input
                              className="form__checkbox__input"
                              type="checkbox"
                              id="ImageSliceSync"
                              name="ImageSliceSync"
                              checked={values.ImageSliceSync}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <label className="form__checkbox__label mr-5" htmlFor="ImageSliceSync">
                              Image Slice Sync
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="formCol">
                    <div className="form__Field form__Field formRow formRow__2">
                      <div className="form__checkboxSubField">
                        <div className="form__checkbox">
                          <div className="form__checkbox__option m_0">
                            <input
                              className="form__checkbox__input"
                              type="checkbox"
                              id="ImageOverlayViewer"
                              name="ImageOverlayViewer"
                              checked={values.ImageOverlayViewer}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <label className="form__checkbox__label mr-5" htmlFor="ImageOverlayViewer">
                              Image Overlay Viewer
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="formRow formRow__3">
                  <div className="formCol">
                    <div className="form__Field form__Field formRow formRow__2">
                      <div className="form__checkboxSubField">
                        <div className="form__checkbox">
                          <div className="form__checkbox__option m_0">
                            <input
                              className="form__checkbox__input"
                              type="checkbox"
                              id="StackScroll"
                              name="StackScroll"
                              checked={values.StackScroll}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <label className="form__checkbox__label mr-5" htmlFor="StackScroll">
                              Stack Scroll
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="formCol">
                    <div className="form__Field form__Field formRow formRow__2">
                      <div className="form__checkboxSubField">
                        <div className="form__checkbox">
                          <div className="form__checkbox__option m_0">
                            <input
                              className="form__checkbox__input"
                              type="checkbox"
                              id="invert"
                              name="invert"
                              checked={values.invert}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <label className="form__checkbox__label mr-5" htmlFor="invert">
                              Invert
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="formCol">
                    <div className="form__Field form__Field formRow formRow__2">
                      <div className="form__checkboxSubField">
                        <div className="form__checkbox">
                          <div className="form__checkbox__option m_0">
                            <input
                              className="form__checkbox__input"
                              type="checkbox"
                              id="Probe"
                              name="Probe"
                              checked={values.Probe}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <label className="form__checkbox__label mr-5" htmlFor="Probe">
                              Probe
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="formRow formRow__3">
                  <div className="formCol">
                    <div className="form__Field form__Field formRow formRow__2">
                      <div className="form__checkboxSubField">
                        <div className="form__checkbox">
                          <div className="form__checkbox__option m_0">
                            <input
                              className="form__checkbox__input"
                              type="checkbox"
                              id="Cine"
                              name="Cine"
                              checked={values.Cine}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <label className="form__checkbox__label mr-5" htmlFor="Cine">
                              Cine
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="formCol">
                    <div className="form__Field form__Field formRow formRow__2">
                      <div className="form__checkboxSubField">
                        <div className="form__checkbox">
                          <div className="form__checkbox__option m_0">
                            <input
                              className="form__checkbox__input"
                              type="checkbox"
                              id="Angle"
                              name="Angle"
                              checked={values.Angle}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <label className="form__checkbox__label mr-5" htmlFor="Angle">
                              Angle
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="formCol">
                    <div className="form__Field form__Field formRow formRow__2">
                      <div className="form__checkboxSubField">
                        <div className="form__checkbox">
                          <div className="form__checkbox__option m_0">
                            <input
                              className="form__checkbox__input"
                              type="checkbox"
                              id="CobbAngle"
                              name="CobbAngle"
                              checked={values.CobbAngle}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <label className="form__checkbox__label mr-5" htmlFor="CobbAngle">
                              Cobb Angle
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>


                <div className="formRow formRow__3">
                  <div className="formCol">
                    <div className="form__Field form__Field formRow formRow__2">
                      <div className="form__checkboxSubField">
                        <div className="form__checkbox">
                          <div className="form__checkbox__option m_0">
                            <input
                              className="form__checkbox__input"
                              type="checkbox"
                              id="Magnify"
                              name="Magnify"
                              checked={values.Magnify}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <label className="form__checkbox__label mr-5" htmlFor="Magnify">
                            Magnify
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="formCol">
                    <div className="form__Field form__Field formRow formRow__2">
                      <div className="form__checkboxSubField">
                        <div className="form__checkbox">
                          <div className="form__checkbox__option m_0">
                            <input
                              className="form__checkbox__input"
                              type="checkbox"
                              id="CalibrationLine"
                              name="CalibrationLine"
                              checked={values.CalibrationLine}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <label className="form__checkbox__label mr-5" htmlFor="CalibrationLine">
                            Calibration Line
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="formCol">
                    <div className="form__Field form__Field formRow formRow__2">
                      <div className="form__checkboxSubField">
                        <div className="form__checkbox">
                          <div className="form__checkbox__option m_0">
                            <input
                              className="form__checkbox__input"
                              type="checkbox"
                              id="TagBrowser"
                              name="TagBrowser"
                              checked={values.TagBrowser}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <label className="form__checkbox__label mr-5" htmlFor="TagBrowser">
                              Tag Browser
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="formRow formRow__3">
                  <div className="formCol">
                    <div className="form__Field form__Field formRow formRow__2">
                      <div className="form__checkboxSubField">
                        <div className="form__checkbox">
                          <div className="form__checkbox__option m_0">
                            <input
                              className="form__checkbox__input"
                              type="checkbox"
                              id="AdvancedMagnify"
                              name="AdvancedMagnify"
                              checked={values.AdvancedMagnify}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <label className="form__checkbox__label mr-5" htmlFor="AdvancedMagnify">
                              Advanced Magnify
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="formCol">
                    <div className="form__Field form__Field formRow formRow__2">
                      <div className="form__checkboxSubField">
                        <div className="form__checkbox">
                          <div className="form__checkbox__option m_0">
                            <input
                              className="form__checkbox__input"
                              type="checkbox"
                              id="UltrasoundDirectionalTool"
                              name="UltrasoundDirectionalTool"
                              checked={values.UltrasoundDirectionalTool}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <label className="form__checkbox__label mr-5" htmlFor="UltrasoundDirectionalTool">
                              Ultrasound Directional Tool
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="formCol">
                    <div className="form__Field form__Field formRow formRow__2">
                      <div className="form__checkboxSubField">
                        <div className="form__checkbox">
                          <div className="form__checkbox__option m_0">
                            <input
                              className="form__checkbox__input"
                              type="checkbox"
                              id="WindowLevelRegion"
                              name="WindowLevelRegion"
                              checked={values.WindowLevelRegion}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <label className="form__checkbox__label mr-5" htmlFor="WindowLevelRegion">
                              Window Level Region
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="formRow formRow__3">
                  <div className="formCol">
                    <div className="form__Field form__Field formRow formRow__2">
                      <div className="form__checkboxSubField">
                        <div className="form__checkbox">
                          <div className="form__checkbox__option m_0">
                            <input
                              className="form__checkbox__input"
                              type="checkbox"
                              id="toggleOverlay"
                              name="toggleOverlay"
                              checked={values.toggleOverlay}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <label className="form__checkbox__label mr-5" htmlFor="toggleOverlay">
                              Toggle Overlay
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="formCol">
                    <div className="form__Field form__Field formRow formRow__2">
                      <div className="form__checkboxSubField">
                        <div className="form__checkbox">
                          <div className="form__checkbox__option m_0">
                            <input
                              className="form__checkbox__input"
                              type="checkbox"
                              id="MeasurementTools"
                              name="MeasurementTools"
                              checked={values.MeasurementTools}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <label className="form__checkbox__label mr-5" htmlFor="MeasurementTools">
                              Measurement Tools
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="formCol">
                    <div className="form__Field form__Field formRow formRow__2">
                      <div className="form__checkboxSubField">
                        <div className="form__checkbox">
                          <div className="form__checkbox__option m_0">
                            <input
                              className="form__checkbox__input"
                              type="checkbox"
                              id="MoreTools"
                              name="MoreTools"
                              checked={values.MoreTools}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <label className="form__checkbox__label mr-5" htmlFor="MoreTools">
                              More Tools
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="formRow formRow__3">
                    <div className="formCol">
                      <div className="form__Field form__Field formRow formRow__2">
                        <div className="form__checkboxSubField">
                          <div className="form__checkbox">
                            <div className="form__checkbox__option m_0">
                              <input
                                className="form__checkbox__input"
                                type="checkbox"
                                id="copyToClipboard"
                                name="copyToClipboard"
                                checked={values.copyToClipboard}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                              <label className="form__checkbox__label mr-5" htmlFor="copyToClipboard">
                                Copy To Clipboard
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <Section title={t('Overlay Setting')}>
                  <div className="formRow formRow__3">
                    <div className="formCol">
                      <div className="form__Field form__Field formRow formRow__2">
                        <div className="form__checkboxSubField">
                          <div className="form__checkbox">
                            <div className="form__checkbox__option m_0">
                              <input
                                className="form__checkbox__input"
                                type="checkbox"
                                id="StudyPatientName"
                                name="StudyPatientName"
                                checked={values.StudyPatientName}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                              <label className="form__checkbox__label mr-5" htmlFor="StudyPatientName">
                                Patient Name
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="formCol">
                      <div className="form__Field form__Field formRow formRow__2">
                        <div className="form__checkboxSubField">
                          <div className="form__checkbox">
                            <div className="form__checkbox__option m_0">
                              <input
                                className="form__checkbox__input"
                                type="checkbox"
                                id="PatientId"
                                name="PatientId"
                                checked={values.PatientId}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                              <label className="form__checkbox__label mr-5" htmlFor="PatientId">
                                Patient Id
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="formCol">
                      <div className="form__Field form__Field formRow formRow__2">
                        <div className="form__checkboxSubField">
                          <div className="form__checkbox">
                            <div className="form__checkbox__option m_0">
                              <input
                                className="form__checkbox__input"
                                type="checkbox"
                                id="PatientBirthDate"
                                name="PatientBirthDate"
                                checked={values.PatientBirthDate}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                              <label className="form__checkbox__label mr-5" htmlFor="PatientBirthDate">
                                Patient BirthDate
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="formRow formRow__3">
                    <div className="formCol">
                      <div className="form__Field form__Field formRow formRow__2">
                        <div className="form__checkboxSubField">
                          <div className="form__checkbox">
                            <div className="form__checkbox__option m_0">
                              <input
                                className="form__checkbox__input"
                                type="checkbox"
                                id="PatientSex"
                                name="PatientSex"
                                checked={values.PatientSex}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                              <label className="form__checkbox__label mr-5" htmlFor="PatientSex">
                                Patient Sex
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="formCol">
                      <div className="form__Field form__Field formRow formRow__2">
                        <div className="form__checkboxSubField">
                          <div className="form__checkbox">
                            <div className="form__checkbox__option m_0">
                              <input
                                className="form__checkbox__input"
                                type="checkbox"
                                id="StudyID"
                                name="StudyID"
                                checked={values.StudyID}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                              <label className="form__checkbox__label mr-5" htmlFor="StudyID">
                              Study Id
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="formCol">
                      <div className="form__Field form__Field formRow formRow__2">
                        <div className="form__checkboxSubField">
                          <div className="form__checkbox">
                            <div className="form__checkbox__option m_0">
                              <input
                                className="form__checkbox__input"
                                type="checkbox"
                                id="StudyDate"
                                name="StudyDate"
                                checked={values.StudyDate}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                              <label className="form__checkbox__label mr-5" htmlFor="StudyDate">
                                Study Date
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="formRow formRow__3">
                    <div className="formCol">
                      <div className="form__Field form__Field formRow formRow__2">
                        <div className="form__checkboxSubField">
                          <div className="form__checkbox">
                            <div className="form__checkbox__option m_0">
                              <input
                                className="form__checkbox__input"
                                type="checkbox"
                                id="ZoomLevel"
                                name="ZoomLevel"
                                checked={values.ZoomLevel}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                              <label className="form__checkbox__label mr-5" htmlFor="ZoomLevel">
                                Zoom Level
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="formCol">
                      <div className="form__Field form__Field formRow formRow__2">
                        <div className="form__checkboxSubField">
                          <div className="form__checkbox">
                            <div className="form__checkbox__option m_0">
                              <input
                                className="form__checkbox__input"
                                type="checkbox"
                                id="WindowLevelOverlay"
                                name="WindowLevelOverlay"
                                checked={values.WindowLevelOverlay}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                              <label className="form__checkbox__label mr-5" htmlFor="WindowLevelOverlay">
                                WindowLevel Overlay
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="formCol">
                      <div className="form__Field form__Field formRow formRow__2">
                        <div className="form__checkboxSubField">
                          <div className="form__checkbox">
                            <div className="form__checkbox__option m_0">
                              <input
                                className="form__checkbox__input"
                                type="checkbox"
                                id="InstanceNumber"
                                name="InstanceNumber"
                                checked={values.InstanceNumber}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                              <label className="form__checkbox__label mr-5" htmlFor="InstanceNumber">
                                Instance Number
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Section>


                <div className="flex flex-row justify-between">
                  <div></div>
                  <div className="flex flex-row">
                    <Button type={ButtonEnums.type.primary}
                      className='btn__white mr-2 border border-black'
                      onClick={onCancelHandler}
                    >
                      {t('Cancel')}
                    </Button>
                    <Button type={ButtonEnums.type.primary}>
                      {t('Save')}
                    </Button>
                  </div>
                </div>
              </Form>
            </>
        )}}
      </Formik>
    </div>
    <Loader isLoaderOpen={toolButtonToggleResponse?.loading} />
    </>
  );
};

const noop = () => {};

UserPreferences.propTypes = {
  disabled: PropTypes.bool,
  hotkeyDefaults: PropTypes.object.isRequired,
  hotkeyDefinitions: PropTypes.object.isRequired,
  onCancel: PropTypes.func,
  onSubmit: PropTypes.func,
  onReset: PropTypes.func,
  hotkeysModule: PropTypes.shape({
    initialize: PropTypes.func.isRequired,
    pause: PropTypes.func.isRequired,
    unpause: PropTypes.func.isRequired,
    startRecording: PropTypes.func.isRequired,
    record: PropTypes.func.isRequired,
  }).isRequired,
};

export default UserPreferences;
